import type { FunctionComponent, HTMLAttributes } from "preact";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { getPhotoImageData, getPhotoImageURL } from "../../store/task/photos.ts";

type PhotoImageProps = HTMLAttributes<HTMLImageElement> & {
    photoId: number;
    mode?: "thumbnail" | "video" | "view" | "4k" | "download";
};

export const PhotoImage: FunctionComponent<PhotoImageProps> = (props) => {
    const [isBlobUrl, setIsBlobUrl] = useState(false);
    const [imgUrl, setImageUrl] = useState("");
    const requestIdRef = useRef(0);

    const fetchPhotoImage = useMemo(() => async (requestId: number) => {
        try {
            const data = await getPhotoImageData(props.photoId, props.mode);
            const url = URL.createObjectURL(new Blob([data]));
            if (requestIdRef.current !== requestId) {
                URL.revokeObjectURL(url);
                return;
            }
            setIsBlobUrl(true);
            setImageUrl(url);
        } catch {
            // try to use the url directly
            const url = await getPhotoImageURL(props.photoId, props.mode);
            if (requestIdRef.current !== requestId) {
                return;
            }
            setIsBlobUrl(false);
            setImageUrl(url);
        }
    }, [props.photoId, props.mode]);

    useEffect(() => {
        const requestId = ++requestIdRef.current;
        setIsBlobUrl(false);
        setImageUrl("");
        fetchPhotoImage(requestId);
    }, [props.photoId, props.mode]);

    useEffect(() => {
        return () => {
            if (isBlobUrl) {
                URL.revokeObjectURL(imgUrl);
            }
        };
    }, [isBlobUrl, imgUrl]);

    return (
        <>
            {imgUrl
                ? <img decoding="async" referrerpolicy="no-referrer" src={imgUrl} />
                : <article aria-busy="true" style="box-shadow: none;"></article>}
        </>
    );
};
export default PhotoImage;
